import { Hash, Plus, X } from 'lucide-react'
import { useEffect, useState } from 'react'
import MainLayout from '../components/layout/MainLayout'
import PostCard from '../components/social/PostCard'
import type { Post } from '../services/socialApi'
import { getPosts, getTrendingHashtags } from '../services/socialApi'

const STORAGE_KEY = 'runai_subscribed_topics'

function getSubscribed(): string[] {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) ?? '[]') as string[]
  } catch {
    return []
  }
}

function saveSubscribed(topics: string[]) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(topics))
}

export default function SocialFollowing() {
  const [subscribed, setSubscribed] = useState<string[]>(getSubscribed)
  const [activeTag, setActiveTag] = useState<string | null>(null)
  const [posts, setPosts] = useState<Post[]>([])
  const [loading, setLoading] = useState(false)
  const [trendingHashtags, setTrendingHashtags] = useState<{ tag: string; post_count: number }[]>([])
  const [showDiscover, setShowDiscover] = useState(false)

  useEffect(() => {
    getTrendingHashtags(20).then(setTrendingHashtags).catch(() => {})
  }, [])

  // Hic subscribe yoksa Discover panelini otomatik ac
  useEffect(() => {
    if (subscribed.length === 0 && trendingHashtags.length > 0) {
      setShowDiscover(true)
    }
  }, [subscribed.length, trendingHashtags.length])

  useEffect(() => {
    if (!activeTag) {
      setPosts([])
      return
    }
    setLoading(true)
    getPosts({ hashtag: activeTag, per_page: 50 })
      .then(d => { setPosts(d.posts) })
      .catch(() => { setPosts([]) })
      .finally(() => { setLoading(false) })
  }, [activeTag])

  const subscribe = (tag: string) => {
    if (subscribed.includes(tag)) { return }
    const next = [...subscribed, tag]
    setSubscribed(next)
    saveSubscribed(next)
    setActiveTag(tag)
    setShowDiscover(false)
  }

  const unsubscribe = (tag: string) => {
    const next = subscribed.filter(t => t !== tag)
    setSubscribed(next)
    saveSubscribed(next)
    if (activeTag === tag) { setActiveTag(next[0] ?? null) }
  }

  const unsubscribedTrending = trendingHashtags.filter(h => !subscribed.includes(h.tag))

  return (
    <MainLayout>
      <div style={{ maxWidth: '860px', margin: '0 auto', padding: '24px' }}>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
          <div>
            <h2 style={{ fontSize: '20px', fontWeight: '700', color: 'var(--text-primary)' }}>Subscribed Topics</h2>
            <p style={{ fontSize: '13px', color: '#94a3b8', marginTop: '4px' }}>Follow hashtags to see relevant posts</p>
          </div>
          <button
            onClick={() => setShowDiscover(!showDiscover)}
            style={{
              display: 'flex', alignItems: 'center', gap: '6px',
              padding: '8px 14px', borderRadius: '8px', fontSize: '13px', fontWeight: '600',
              background: 'linear-gradient(135deg, #667eea, #764ba2)',
              border: 'none', color: '#fff', cursor: 'pointer'
            }}
          >
            <Plus size={15} /> Discover Topics
          </button>
        </div>

        {/* Discover panel */}
        {showDiscover && (
          <div style={{
            background: 'var(--card-bg)', border: '1px solid var(--border-color)',
            borderRadius: '12px', padding: '20px', marginBottom: '20px'
          }}>
            <div style={{ fontSize: '13px', fontWeight: '700', color: 'var(--text-primary)', marginBottom: '12px' }}>
              Trending Topics
            </div>
            {unsubscribedTrending.length === 0 && (
              <div style={{ fontSize: '13px', color: '#94a3b8' }}>You are subscribed to all trending topics.</div>
            )}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {unsubscribedTrending.map(h => (
                <button
                  key={h.tag}
                  onClick={() => subscribe(h.tag)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '5px',
                    padding: '6px 12px', borderRadius: '20px', fontSize: '13px', fontWeight: '500',
                    background: '#f8fafc', border: '1px solid #e2e8f0',
                    color: '#64748b', cursor: 'pointer', transition: 'all 0.15s'
                  }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = '#667eea'; e.currentTarget.style.color = '#667eea' }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = '#e2e8f0'; e.currentTarget.style.color = '#64748b' }}
                >
                  <Plus size={13} /> #{h.tag}
                  <span style={{ fontSize: '11px', opacity: 0.6 }}>{h.post_count}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Subscribed tags */}
        {subscribed.length === 0 ? (
          <div style={{
            textAlign: 'center', padding: '64px 24px',
            background: 'var(--card-bg)', border: '1px solid var(--border-color)',
            borderRadius: '12px'
          }}>
            <Hash size={40} style={{ color: '#cbd5e1', margin: '0 auto 16px', display: 'block' }} />
            <div style={{ fontSize: '16px', fontWeight: '600', color: 'var(--text-primary)', marginBottom: '8px' }}>
              No topics yet
            </div>
            <div style={{ fontSize: '13px', color: '#94a3b8', marginBottom: '20px' }}>
              Subscribe to hashtags to see posts from those topics
            </div>
            <button
              onClick={() => setShowDiscover(true)}
              style={{
                padding: '10px 20px', borderRadius: '8px', fontSize: '13px', fontWeight: '600',
                background: 'linear-gradient(135deg, #667eea, #764ba2)',
                border: 'none', color: '#fff', cursor: 'pointer'
              }}
            >
              Discover Topics
            </button>
          </div>
        ) : (
          <>
            {/* Tag pills */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '20px' }}>
              {subscribed.map(tag => (
                <div
                  key={tag}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '4px',
                    padding: '6px 12px', borderRadius: '20px', fontSize: '13px', fontWeight: '600',
                    background: activeTag === tag ? 'linear-gradient(135deg, #667eea, #764ba2)' : '#f8fafc',
                    border: activeTag === tag ? 'none' : '1px solid #e2e8f0',
                    color: activeTag === tag ? '#fff' : '#64748b',
                    cursor: 'pointer', transition: 'all 0.15s'
                  }}
                >
                  <span onClick={() => setActiveTag(tag)}>#{tag}</span>
                  <button
                    onClick={() => unsubscribe(tag)}
                    style={{
                      background: 'none', border: 'none', cursor: 'pointer',
                      color: activeTag === tag ? 'rgba(255,255,255,0.7)' : '#94a3b8',
                      padding: '0 0 0 2px', display: 'flex', alignItems: 'center'
                    }}
                  >
                    <X size={12} />
                  </button>
                </div>
              ))}
            </div>

            {/* Posts */}
            {!activeTag && (
              <div style={{ textAlign: 'center', padding: '32px', color: '#94a3b8', fontSize: '13px' }}>
                Select a topic above to see posts
              </div>
            )}
            {loading && (
              <div style={{ textAlign: 'center', padding: '32px', color: '#667eea' }}>Loading...</div>
            )}
            {!loading && activeTag && posts.length === 0 && (
              <div style={{ textAlign: 'center', padding: '48px', color: '#64748b' }}>
                <div style={{ fontSize: '14px', fontWeight: '600', marginBottom: '8px' }}>No posts for #{activeTag}</div>
                <div style={{ fontSize: '13px' }}>Be the first to post about this topic</div>
              </div>
            )}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {posts.map(post => <PostCard key={post.id} post={post} />)}
            </div>
          </>
        )}
      </div>
    </MainLayout>
  )
}
